// libs
import React, { useEffect, useState } from 'react';
import {
  Box,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

// src
import { getFloorPlanImage } from '@utils/helperFunctions';
import { photoAssetUrl } from '@foxtons/fdl/utils/index';

import { PropertySaveButton } from './PropertySaveButton';
import { ResponsiveCarousel } from '@foxtons/fdl/components/ResponsiveCarousel/index';

const VideoPlayerBox = dynamic(() => import('@components/VideoPlayer'), {
  ssr: false
});
const DetailFloorPlan = dynamic(
  () => import('@foxtons/fdl/components/DetailFloorPlan/index'),
  {
    ssr: false
  }
);
const PropertyDetailLocationMap = dynamic(
  () => import('@components/PropertyDetailLocationMap'),
  {
    ssr: false
  }
);
const preloadMap = () => import('@components/PropertyDetailLocationMap');

// styles
import styles from './styles';
import Button from '@foxtons/fdl/components/Button/index';
import ComponentWrapper from '@components/ComponentWrapper';
import { useUserFromStorage } from '@foxtons/fdl/utils/customHooks/useUserFromStorage';
/* Makes room for sticky 'Book a Viewing' button
  when scrolled to the very bottom */
const globalStyles = (
  <GlobalStyles
    styles={({ spacing, breakpoints }) => ({
      'footer.footerContainer': {
        [breakpoints.down('md')]: {
          paddingBottom: spacing(5)
        }
      }
    })}
  />
);

interface Photos {
  filename: string;
  propertyReference: string;
  timestamp: string;
}

interface Videos {
  url: string;
}

export interface AssetInfo {
  assets: {
    photos: Photos[];
    videos: Videos[];
    floorplan: {
      large: {
        filename: string;
        timestamp: string;
      };
      small: {
        filename: string;
        timestamp: string;
      };
    };
  };
}

export interface PropertyBlob {
  description: string;
  locationName: string;
  bulletPoints: string[];
  assetInfo: AssetInfo;
}

export interface PropertyDetails {
  instructionType: string;
  priceTo: number;
  priceFrom: number;
  bedrooms: number;
  bathrooms: number;
  hasVideo: number;
  hasVirtualTour: number;
  isReduced: number;
  postcodeShort: string;
  streetName: string;
  type: string;
  propertyBlob: PropertyBlob;
  location?: {
    lat: string;
    lon: string;
  };
  class: string;
  isNewHome: boolean;
  propertyReference: string;
  isSneakPeek: boolean;
}

interface Tab {
  value: string;
  label: string;
  mobileLabel?: string;
}

const MEDIA_TABS: Tab[] = [
  {
    value: 'photos',
    label: 'Photos'
  },
  {
    value: 'floor-plan',
    label: 'Floorplan'
  },
  {
    value: 'videos',
    label: 'Video'
  },
  {
    value: 'location',
    label: 'Location'
  },
  {
    value: 'tour',
    label: 'Virtual Tour',
    mobileLabel: '3D Tour'
  }
];

type RenderVideo = (props: {
  setShowVideo: (e: boolean) => void;
  isActive: boolean;
  url: string;
}) => JSX.Element;

type RenderExtraMenu = (props: { activeTab: string }) => JSX.Element;

export default function PropertyDetailMedia({
  containerStyles,
  propertyDetails,
  initialTab = 'photos',
  mediaTabs = MEDIA_TABS,
  renderVideo,
  renderExtraMenu,
  afterTabChanged,
  validateTabChange,
  handleSaveProperty,
  isFavProperty
}: {
  containerStyles: SxProps;
  propertyDetails: PropertyDetails;
  initialTab?: string;
  mediaTabs?: Tab[];
  renderVideo?: RenderVideo;
  renderExtraMenu?: RenderExtraMenu;
  afterTabChanged?: (tab: string) => void;
  validateTabChange?: (tab: string) => boolean;
  handleSaveProperty: (typeInstruction: string, propRef: string) => void;
  isFavProperty: boolean;
}) {
  const [user] = useUserFromStorage();
  const router = useRouter();

  const [activeTab, setActiveTab] = useState(initialTab);
  const [showVideo, setShowVideo] = useState(true);
  const [visitedTabs, setVisitedTabs] = useState([initialTab]);
  const {
    hasVideo,
    hasVirtualTour,
    propertyBlob,
    location,
    isNewHome,
    isSneakPeek
  } = propertyDetails;
  const { assetInfo } = propertyBlob;
  const { assets } = assetInfo;
  const { photos } = assets;
  const hasRealVideo = hasVideo && (assets.videos ?? []).length > 0;

  const imagesData =
    photos.length > 0
      ? photos.map((photo) => ({
          id: photo.filename,
          title: photo.filename,
          srcSet: photoAssetUrl(
            photo.propertyReference,
            photo.filename,
            photo.timestamp,
            isSneakPeek,
            '1600'
          )
        }))
      : [
          {
            id: isSneakPeek ? 'sneak_peak_image' : 'default_image',
            title: isSneakPeek ? 'sneak_peak_image' : 'default_image',
            srcSet: isSneakPeek ? '/webImages/holdingSneakPeek.svg' : ''
          }
        ];

  const classType = propertyDetails.class;
  const newHome = classType === 'unit' && isNewHome ? true : false;

  const theme = useTheme();
  const isLaptopOrSmaller = useMediaQuery(theme.breakpoints.down('md'));

  const searchParams = new URL(router.asPath, process.env.NEXT_PUBLIC_URL)
    .searchParams;
  const tabFromParams = searchParams.get('tab');

  useEffect(() => {
    if (tabFromParams === 'video' && hasRealVideo && user?.contactId) {
      setActiveTab('videos');

      setVisitedTabs((prev) => {
        if (prev.includes('videos')) {
          return prev;
        }

        return [...prev, 'videos'];
      });
    }
  }, [hasRealVideo, tabFromParams, user?.contactId]);

  const renderComponent = () => {
    return (
      <>
        {!visitedTabs.includes('photos') ? null : (
          <Box display={activeTab === 'photos' ? 'block' : 'none'}>
            <ComponentWrapper otherProps={{ photos }}>
              <ResponsiveCarousel
                keepTrackOfActiveIndex
                isEagerImg
                isPropertyDetail
                keyboardNavigation
                disableDotsControls={false}
                dataImg={imagesData}
                photoAspectRatio="1 / 0.67" // property detail photo aspect ratio
              />
            </ComponentWrapper>
            {newHome && (
              <Typography sx={styles.messageDisclaimer}>
                All images used for indicative purposes only
              </Typography>
            )}
          </Box>
        )}
        {!visitedTabs.includes('videos')
          ? null
          : renderVideo
          ? renderVideo({
              setShowVideo,
              isActive: activeTab === 'videos',
              url: `${assetInfo.assets.videos[0]?.url}`
            })
          : user?.contactId && (
              <VideoPlayerBox
                url={`${assetInfo.assets.videos[0]?.url}`}
                videoWrapper={{
                  height: '100%',
                  display: activeTab === 'videos' ? 'inline' : 'none'
                }}
                errorWrapper={{
                  height: '100%',
                  display: activeTab === 'videos' ? 'inline' : 'none'
                }}
                setShow={(e) => setShowVideo(e)}
                isPaused={activeTab !== 'videos'}
              />
            )}
        {!visitedTabs.includes('floor-plan') ? null : (
          <Box display={activeTab === 'floor-plan' ? 'inline' : 'none'}>
            <DetailFloorPlan
              img={getFloorPlanImage(assets)}
              isReactApp={false}
            />
          </Box>
        )}
        {!visitedTabs.includes('location') ? null : (
          <Box
            marginTop="1rem"
            display={activeTab === 'location' ? 'inline' : 'none'}
          >
            <PropertyDetailLocationMap
              location={{
                lat: Number(location?.lat || '0'),
                lng: Number(location?.lon || '0')
              }}
              propertyDetailsMap={[propertyDetails]}
              isPropertyDetailPage
              mapWrapperStyles={styles.mapStylesCustom}
            />
          </Box>
        )}
        {!visitedTabs.includes('tour') ? null : (
          <Box display={activeTab === 'tour' ? 'inline' : 'none'}>
            <iframe
              title="property-tour"
              className="vimeo_video"
              data-src={assetInfo.assets.tour3D.tourUrl}
              allow="autoplay; fullscreen"
              allowFullScreen
              src={assetInfo.assets.tour3D.tourUrl}
              frameBorder="0"
            />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        ...((activeTab === 'videos' && showVideo) || activeTab === 'tour'
          ? styles.mediaDetailVideo
          : isNewHome
          ? styles.mediaDetailsContainerNewHomes
          : styles.mediaDetailsContainer),
        ...(containerStyles || {})
      }}
    >
      <Box
        sx={styles.container}
        id="content-container"
        data-active-tab={activeTab}
      >
        <PropertySaveButton
          handleSaveProperty={handleSaveProperty}
          isFavProperty={isFavProperty}
        />

        {renderComponent()}
      </Box>
      <Box
        id="content-control-container"
        sx={styles.contentContainer}
        onMouseEnter={preloadMap}
      >
        <Box sx={styles.controls}>
          {mediaTabs.map((tab: Tab) => {
            if (tab.value === 'videos' && !hasRealVideo) {
              return null;
            }

            if (tab.value === 'tour' && !hasVirtualTour) {
              return null;
            }

            if (
              tab.value === 'floor-plan' &&
              !assets?.floorplan?.large?.filename
            ) {
              return null;
            }

            return (
              <Button
                key={tab.value}
                variant="outlined"
                sx={
                  tab.value === activeTab
                    ? styles.selectedControlItem
                    : styles.controlItem
                }
                onClick={() => {
                  if (validateTabChange && !validateTabChange(tab.value)) {
                    return;
                  }

                  setActiveTab(tab.value);

                  if (afterTabChanged) {
                    afterTabChanged(tab.value);
                  }

                  if (!visitedTabs.includes(tab.value)) {
                    setVisitedTabs((prev) => [...prev, tab.value]);
                  }
                }}
              >
                <Typography
                  sx={
                    tab.value === activeTab
                      ? styles.selectedControlItemText
                      : styles.controlItemText
                  }
                  variant="body1"
                >
                  {isLaptopOrSmaller && tab.mobileLabel
                    ? tab.mobileLabel
                    : tab.label}
                </Typography>
              </Button>
            );
          })}
        </Box>
      </Box>
      {renderExtraMenu && renderExtraMenu({ activeTab })}
      {globalStyles}
    </Box>
  );
}
